import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { BACKEND_URL } from "./apiBase";

const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "alphalane_customer_token";

// Auth axios client — session lives in the httpOnly cookie only (XSS-safe).
// Frontend and /api are served same-origin (prod + preview), so the cookie
// is always first-party and reliably sent with withCredentials.
const authClient = axios.create({ baseURL: API, withCredentials: true });

const AuthContext = createContext({
  user: null,
  refresh: async () => {},
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  patchProgress: async () => {},
});

// httpOnly-Cookie-Migration: Alt-Tokens aus localStorage entfernen.
const clearToken = () => {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
};
clearToken();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await authClient.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      clearToken();
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await authClient.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    return (await authClient.post("/auth/register", payload)).data;
  }, []);

  const logout = useCallback(async () => {
    try { await authClient.post("/auth/logout"); } catch { /* noop */ }
    clearToken();
    setUser(false);
  }, []);

  const patchProgress = useCallback(async (payload) => {
    const { data } = await authClient.patch("/auth/me/onboarding", payload);
    setUser(data);
    return data;
  }, []);

  const patchMailLanguage = useCallback(async (lang) => {
    const { data } = await authClient.patch("/auth/me/mail-language", { mail_language: lang });
    setUser(data);
    return data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, refresh, login, register, logout, patchProgress, patchMailLanguage }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Helper to format API error detail (FastAPI 422 returns array)
export function formatApiError(detail) {
  if (detail == null) return "Etwas ist schiefgelaufen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Export the shared authClient so other pages (like Dashboard) can use it
// with automatic Bearer-header injection.
export { authClient };
